/**
 * DecisionAdvicePopover.js — RC 1.28.R3 · extended in RC 1.28.R4.
 *
 * Generalised from `RepositioningAdvicePopover` (RC 1.28.R2-lite).
 * Wraps ANY of the four decision badges on the Jobs list
 * (`apply`, `apply_with_repositioning`, `hold`, `reject`) with a
 * Radix popover that surfaces NDOR-generated per-decision guidance
 * read from the `user_score` fields the `/api/jobs` response now
 * plucks out of `reasoning_json`. When no string is available for a
 * row, a single shared fallback copy is shown.
 *
 * RC 1.28.R4 — also renders the backend-derived
 * `user_score.decision_confidence` (`high` / `medium` / `low`) as a
 * subtle coloured chip under the guidance text. The chip is a read
 * of an already-derived field — no scoring or decision logic lives
 * in this component.
 *
 * Why controlled Radix?
 * ---------------------
 * The badge lives inside a `<Link to=/jobs/:id>` row wrapper. The
 * naive "uncontrolled Radix + stopPropagation in onClick" approach
 * leaks native anchor navigation, and `preventDefault()` makes
 * Radix bail out (`event.defaultPrevented`). We therefore run the
 * popover in controlled mode and drive `open` from our own click
 * handler after cancelling the event.
 *
 * Props:
 *   advice      (string | null | undefined)  per-decision string
 *                                             plucked by the backend.
 *   decision    (string)                     one of `shortlist`,
 *                                             `apply_with_repositioning`,
 *                                             `hold`, `reject`.
 *   confidence  ('high' | 'medium' | 'low' | null | undefined)
 *                                             backend-derived tier.
 *   badgeLabel  (string)                     rendered inside the pill.
 *   className   (string)                     tailwind tones for the pill.
 *   testIdBase  (string)                     per-row test-id prefix,
 *                                             e.g. `job-row-advice-<jobId>`.
 */
import { useState } from 'react';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';

const FALLBACK_ADVICE =
  'This decision was made based on your current profile fit. '
  + 'No detailed guidance was captured.';

/** Stable human label for the popover header per decision.
 *  Note: the product decision enum uses `shortlist` (equivalent to
 *  the RC 1.28.R3 spec's "apply" bucket). */
const DECISION_HEADER = {
  shortlist:                'Why shortlist',
  apply_with_repositioning: 'Tailoring advice',
  hold:                     'Why hold',
  reject:                   'Why reject',
};

/** RC 1.28.R4 — subtle colour treatment per confidence tier. Kept
 *  low-key on purpose; these chips should read as metadata, not
 *  steal attention from the decision pill itself. */
const CONFIDENCE_TONE = {
  high:   'bg-ndor-success-light/60 text-ndor-success border-ndor-success/30',
  medium: 'bg-ndor-warning-light/60 text-ndor-warning border-ndor-warning/30',
  low:    'bg-ndor-surface-alt      text-ndor-ink-muted border-ndor-edge',
};
const CONFIDENCE_LABEL = {
  high:   'High',
  medium: 'Medium',
  low:    'Low',
};

export default function DecisionAdvicePopover({
  advice,
  decision,
  confidence,
  badgeLabel,
  className,
  testIdBase,
}) {
  const clean = typeof advice === 'string' ? advice.trim() : '';
  const hasAdvice = clean.length > 0;
  const shown = hasAdvice ? clean : FALLBACK_ADVICE;
  const header = DECISION_HEADER[decision] || 'Decision details';

  const confTone  = CONFIDENCE_TONE[confidence]  || null;
  const confLabel = CONFIDENCE_LABEL[confidence] || null;

  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={header}
          title={hasAdvice ? clean : FALLBACK_ADVICE}
          data-testid={`${testIdBase}-trigger`}
          data-decision={decision}
          data-has-advice={hasAdvice ? 'true' : 'false'}
          data-confidence={confidence || ''}
          onClick={(ev) => {
            // Cancel the wrapping <Link>'s navigation (both the
            // browser's native anchor follow AND react-router's
            // onClick) before toggling our own open state.
            ev.preventDefault();
            ev.stopPropagation();
            setOpen((v) => !v);
          }}
          className="appearance-none bg-transparent border-0 p-0 cursor-pointer"
        >
          <Badge
            variant="outline"
            className={`text-[10px] cursor-pointer ${className}`}
          >
            {badgeLabel}
          </Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        className="w-[320px] text-xs p-3 space-y-2"
        data-testid={`${testIdBase}-content`}
        onClick={(ev) => ev.stopPropagation()}
      >
        <div className="text-[10px] font-mono-ui uppercase tracking-[0.16em] text-ndor-ink-muted">
          {header}
        </div>
        <p
          className="text-[11px] text-ndor-ink leading-relaxed"
          data-testid={`${testIdBase}-body`}
        >
          {shown}
        </p>
        {confLabel && (
          <div
            className="flex items-center gap-1.5 pt-1"
            data-testid={`${testIdBase}-confidence`}
          >
            <span className="text-[10px] font-mono-ui uppercase tracking-[0.16em] text-ndor-ink-muted">
              Confidence:
            </span>
            <span
              className={`inline-flex items-center rounded-md border px-1.5 py-0 text-[10px] font-medium ${confTone}`}
              data-testid={`${testIdBase}-confidence-chip`}
              data-confidence={confidence}
            >
              {confLabel}
            </span>
          </div>
        )}
        {!hasAdvice && (
          <p
            className="text-[10px] italic text-ndor-ink-muted"
            data-testid={`${testIdBase}-fallback-note`}
          >
            No tailored guidance was captured for this analysis.
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}

export { FALLBACK_ADVICE, DECISION_HEADER, CONFIDENCE_TONE, CONFIDENCE_LABEL };
